import { Controller } from '@hotwired/stimulus'
import * as Turbo from '@hotwired/turbo'

const inFlightSubmits = new Set()

export default class extends Controller {
  static targets = [
    'listView',
    'editView',
    'renameInput',
    'swatch',
    'error',
    'deleteForm',
    'setDefaultForm'
  ]

  static values = {
    createUrl: String,
    orderUrl: String,
    teamSqid: String,
    simulationRequestSqid: String,
    defaultColor: String,
    errorMessage: String,
    tooLongErrorMessage: String,
    maxPerTeamErrorMessage: String,
    nameMaxLength: { type: Number, default: 20 },
    maxPerTeam: { type: Number, default: 10 }
  }

  static classes = ['active']

  connect () {
    this.editSqid = null
    this.editUpdateUrl = null
    this.editColor = null
    this.editName = null
    this.creating = false
    this.dropdownElement = this.element.closest('[data-controller~="dropdown-floating"]')
    this.dropdownElement?.addEventListener('dropdown-floating:closed', this.commitCreateOnClose)
  }

  disconnect () {
    this.dropdownElement?.removeEventListener('dropdown-floating:closed', this.commitCreateOnClose)
  }

  commitCreateOnClose = () => {
    if (!this.creating) return

    this.submitName()
    this.showList()
  }

  showList () {
    this.creating = false
    this.editSqid = null
    this.editUpdateUrl = null
    if (this.hasEditViewTarget) this.editViewTarget.hidden = true
    if (this.hasListViewTarget) this.listViewTarget.hidden = false
  }

  createNew () {
    if (this.statusCount() >= this.maxPerTeamValue) {
      this.showError(this.maxPerTeamErrorMessageValue)
      return
    }

    this.creating = true
    this.editSqid = null
    this.editUpdateUrl = null
    this.editColor = this.defaultColorValue
    this.editName = ''
    if (this.hasRenameInputTarget) this.renameInputTarget.value = ''
    this.highlightSwatch()
    this.toggleDeleteForm(false)
    this.toggleSetDefaultForm()
    this.showEdit()
    if (this.hasRenameInputTarget) this.renameInputTarget.focus()
  }

  edit (event) {
    event.preventDefault()
    event.stopPropagation()

    this.creating = false
    this.editSqid = event.params.sqid
    this.editUpdateUrl = event.params.updateUrl
    this.editColor = event.params.color
    this.editName = event.params.name
    if (this.hasRenameInputTarget) this.renameInputTarget.value = event.params.name
    this.highlightSwatch()
    const deletable = event.params.deletable === true || event.params.deletable === 'true'
    this.toggleDeleteForm(deletable)
    this.toggleSetDefaultForm(event.params.setDefaultUrl)
    this.showEdit()
    if (this.hasRenameInputTarget) this.renameInputTarget.focus()
  }

  showEdit () {
    this.clearError()
    if (this.hasListViewTarget) this.listViewTarget.hidden = true
    if (this.hasEditViewTarget) this.editViewTarget.hidden = false
  }

  highlightSwatch () {
    this.swatchTargets.forEach((swatch) => {
      swatch.classList.toggle(this.activeClass, swatch.dataset.color === this.editColor)
    })
  }

  pickColor (event) {
    this.editColor = event.params.color
    this.highlightSwatch()
    if (!this.creating) this.submitEdit()
  }

  commitRename (event) {
    if (event.type === 'keydown') {
      if (event.key !== 'Enter') return
      event.preventDefault()
      this.submitName()
      return
    }

    const name = this.renameValue()
    if (this.creating) return
    if (name && name !== this.editName) this.submitEdit()
  }

  submitName () {
    const name = this.renameValue()
    if (!name) return
    if (!this.nameWithinLimit(name)) return

    if (this.creating) {
      this.submitCreate(name)
    } else {
      this.submitEdit({ returnToList: true })
    }
  }

  async reorder () {
    const ids = Array.from(this.listViewTarget.querySelectorAll('[data-status-id]'))
      .map((row) => row.dataset.statusId)

    const body = new FormData()
    body.append('team_id', this.teamSqidValue)
    ids.forEach((id) => body.append('status_ids[]', id))
    await this.submit(this.orderUrlValue, 'PATCH', body)
  }

  async submitCreate (name) {
    if (!this.nameWithinLimit(name)) return

    this.clearError()
    const body = this.buildBody(name, this.editColor)
    const ok = await this.submit(this.createUrlValue, 'POST', body)
    if (ok) this.showList()
  }

  async submitEdit ({ returnToList = false } = {}) {
    if (!this.editUpdateUrl) return

    const name = this.renameValue()
    if (!name) return
    if (!this.nameWithinLimit(name)) return

    this.clearError()
    const body = this.buildBody(name, this.editColor)
    const ok = await this.submit(this.editUpdateUrl, 'PATCH', body)
    if (!ok) return

    this.editName = name
    if (returnToList) this.showList()
  }

  renameValue () {
    return this.hasRenameInputTarget ? this.renameInputTarget.value.trim() : ''
  }

  toggleDeleteForm (visible) {
    if (!this.hasDeleteFormTarget) return

    this.deleteFormTarget.hidden = !visible
    if (visible && this.editUpdateUrl) {
      this.deleteFormTarget.action = this.editUpdateUrl
    }
  }

  toggleSetDefaultForm (url) {
    if (!this.hasSetDefaultFormTarget) return

    const visible = Boolean(url)
    this.setDefaultFormTarget.hidden = !visible
    if (visible) this.setDefaultFormTarget.action = url
  }

  async setDefault (event) {
    event.preventDefault()
    if (!this.hasSetDefaultFormTarget || !this.setDefaultFormTarget.action) return

    const body = new FormData()
    body.append('simulation_request_id', this.simulationRequestSqidValue)
    const ok = await this.submit(this.setDefaultFormTarget.action, 'PATCH', body)
    if (ok) this.showList()
  }

  buildBody (name, color) {
    const body = new FormData()
    body.append('simulation_request_id', this.simulationRequestSqidValue)
    body.append('simulation_request_status[name]', name)
    body.append('simulation_request_status[color]', color)
    return body
  }

  async submit (url, method, body) {
    const lockKey = this.simulationRequestSqidValue || this.element.id
    if (inFlightSubmits.has(lockKey)) return false
    inFlightSubmits.add(lockKey)

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(url, {
        method,
        headers: {
          'X-CSRF-Token': csrfToken,
          Accept: 'text/vnd.turbo-stream.html'
        },
        body
      })

      if (!response.ok) {
        this.showError(await this.errorFromResponse(response))
        return false
      }

      const text = await response.text()
      if (text) Turbo.renderStreamMessage(text)
      return true
    } finally {
      inFlightSubmits.delete(lockKey)
    }
  }

  nameWithinLimit (name) {
    if (name.length <= this.nameMaxLengthValue) return true

    this.showError(this.tooLongErrorMessageValue)
    return false
  }

  statusCount () {
    return this.element.querySelectorAll('[data-status-id]').length
  }

  async errorFromResponse (response) {
    try {
      const payload = await response.json()
      return payload.error
    } catch {
      return null
    }
  }

  showError (message) {
    const text = message || this.errorMessageValue
    this.errorTargets.forEach((el) => {
      el.textContent = text
      el.hidden = false
    })
  }

  clearError () {
    this.errorTargets.forEach((el) => {
      el.hidden = true
    })
  }
}
